import intersection from 'lodash/intersection';
import { getConfig } from '@/config';
import { PermissionsDefinition } from '@/store/modules/PositionModule';
import { useAuthStore } from '@/store/auth';

export type PermissionsMap = Map<string, string[]>; // This assumes that the permissions are stored as a Map with the subject as key and an array of actions as value

export const useAuthorization = () => {
  const authStore = useAuthStore();

  /**
   * Checks if a user has permission to perform a specific action on a subject within a company.
   *
   * @param {string} companyCode - The company code.
   * @param {Map<string, PermissionsMap> | undefined} currentPermissions - The current permissions, stored in a map.
   * @param {PermissionCheckOptions} options - The options for permission check, including subject and action.
   * @returns {boolean} - Returns true if the user has permission, false otherwise.
   */
  const hasPermission = (
    companyCode: string,
    currentPermissions: Map<string, PermissionsMap> | undefined,
    { subject, action }: PermissionsDefinition,
  ): boolean => {
    if (!currentPermissions) {
      return false;
    }

    // check for company code
    const companyPermissions =
      typeof currentPermissions.get === 'function' && currentPermissions.get(companyCode.toUpperCase());
    if (!companyPermissions) {
      return false;
    }

    // check if subject exists
    const permissionSubject = companyPermissions.get(subject);
    if (!permissionSubject) {
      return false;
    }

    const actions = Array.isArray(action) ? action : [action];

    // action exists
    return intersection(permissionSubject, actions).length > 0;
  };

  /**
   * Checks if the logged user has the specified permission.
   * @param {string|string[]} permission - The permission with action and subject.
   * @returns {boolean} - true if the logged user has the specified permission, false otherwise.
   */
  const hasLoggedUserPermission = (permission: PermissionsDefinition): boolean => {
    const companyCode = getConfig().TENANT;

    return hasPermission(companyCode, authStore.currentAgwUser?.mappedPermissions, permission);
  };

  return {
    hasPermission,
    hasLoggedUserPermission,
  };
};
